import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import QuizMode from "@/pages/QuizMode";
import ScenarioMode from "@/pages/ScenarioMode";
import TradeoffMode from "@/pages/TradeoffMode";
import SystemDesignMode from "@/pages/SystemDesignMode";
import Profile from "@/pages/Profile";
import Leaderboard from "@/pages/Leaderboard";
import Analytics from "@/pages/Analytics";
import Achievements from "@/pages/Achievements";
import PvpLobby from "@/pages/PvpLobby";
import PvpBattle from "@/pages/PvpBattle";
import PvpResult from "@/pages/PvpResult";
import PvpLeaderboard from "@/pages/PvpLeaderboard";
import LearningMode from "@/pages/LearningMode";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AuthCallback() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // This component handles Emergent OAuth callback - session_id is exchanged for session_token
  const hasProcessed = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Use useRef for processed flag to prevent race conditions under StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get('session_id');

    if (!sessionId) {
      console.error('No session_id in URL');
      window.location.href = '/';
      return;
    }

    // Exchange session_id for session_token via backend
    fetch(`${BACKEND_URL}/api/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId })
    })
      .then(res => {
        if (!res.ok) throw new Error('Auth failed');
        return res.json();
      })
      .then(data => {
        console.log('Auth success:', data);
        // Store token in localStorage as fallback for Safari/iOS (SameSite cookie issues)
        if (data.session_token) {
          localStorage.setItem('session_token', data.session_token);
        }
        // Small delay to ensure cookie is set before redirect
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 500);
      })
      .catch(err => {
        console.error('Auth error:', err);
        window.location.href = '/';
      });
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-foreground">Авторизація...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        // Get token from localStorage as fallback for Safari/iOS
        const localToken = localStorage.getItem('session_token');
        const headers = {
          'Accept': 'application/json'
        };
        
        // Add Authorization header if we have a local token (Safari fallback)
        if (localToken) {
          headers['Authorization'] = `Bearer ${localToken}`;
        }
        
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include',
          headers
        });
        
        if (!response.ok) {
          console.log('Auth check failed:', response.status);
          // Clear invalid token
          localStorage.removeItem('session_token');
          throw new Error('Not authenticated');
        }
        
        const userData = await response.json();
        console.log('Auth check success:', userData);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Auth check error:', error);
        localStorage.removeItem('session_token');
        setIsAuthenticated(false);
      }
    };
    
    checkAuth();
  }, [location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('Not authenticated, redirecting to /');
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/quiz" element={
        <ProtectedRoute>
          <QuizMode />
        </ProtectedRoute>
      } />
      <Route path="/scenario" element={
        <ProtectedRoute>
          <ScenarioMode />
        </ProtectedRoute>
      } />
      <Route path="/tradeoff" element={
        <ProtectedRoute>
          <TradeoffMode />
        </ProtectedRoute>
      } />
      <Route path="/design" element={
        <ProtectedRoute>
          <SystemDesignMode />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/leaderboard" element={
        <ProtectedRoute>
          <Leaderboard />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <Analytics />
        </ProtectedRoute>
      } />
      <Route path="/achievements" element={
        <ProtectedRoute>
          <Achievements />
        </ProtectedRoute>
      } />
      <Route path="/pvp" element={
        <ProtectedRoute>
          <PvpLobby />
        </ProtectedRoute>
      } />
      <Route path="/pvp/battle/:matchId" element={
        <ProtectedRoute>
          <PvpBattle />
        </ProtectedRoute>
      } />
      <Route path="/pvp/result/:matchId" element={
        <ProtectedRoute>
          <PvpResult />
        </ProtectedRoute>
      } />
      <Route path="/pvp/leaderboard" element={
        <ProtectedRoute>
          <PvpLeaderboard />
        </ProtectedRoute>
      } />
      <Route path="/learning" element={
        <ProtectedRoute>
          <LearningMode />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;